import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  LocalHospital as LocalHospitalIcon,
} from '@mui/icons-material';

const EmergencyCare: React.FC = () => {
  const [emergencySearchTerm, setEmergencySearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');

  const emergencyAlgorithms = [
    {
      condition: 'Лихорадка у детей',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Температура >38°C', 'Общее недомогание', 'Озноб', 'Потливость'],
      algorithm: [
        'Измерить температуру тела (ректально у детей до 3 лет)',
        'Оценить общее состояние ребенка (активность, аппетит, сон)',
        'При T >38,5°C дать жаропонижающее',
        'Обеспечить обильное питье',
        'Физические методы охлаждения (обтирание водой комнатной температуры)',
        'При ухудшении состояния - госпитализация'
      ],
      medications: [
        'Парацетамол 10-15 мг/кг каждые 6 часов',
        'Ибупрофен 5-10 мг/кг каждые 8 часов (с 3 месяцев)',
        'Физические методы охлаждения'
      ],
      contraindications: ['Аспирин детям до 15 лет', 'Анальгин детям до 15 лет', 'Нимесулид детям до 12 лет'],
      whenToCall: ['T >39°C у детей до 3 мес', 'Фебрильные судороги', 'Нарушение сознания', 'Петехиальная сыпь', 'Ригидность затылочных мышц']
    },
    {
      condition: 'Острая диарея',
      priority: 'medium',
      ageGroup: 'все возраста',
      symptoms: ['Жидкий стул >3 раз/сут', 'Боли в животе', 'Рвота', 'Снижение диуреза'],
      algorithm: [
        'Оценить степень обезвоживания (кожа, слизистые, тургор)',
        'Начать оральную регидратацию (регидрон, глюкосолан)',
        'Продолжить грудное вскармливание',
        'Диета: рисовый отвар, бананы, яблоки',
        'При тяжелом обезвоживании - в/в регидратация'
      ],
      medications: [
        'Регидрон 5-10 мл/кг/час',
        'Смекта 1 пакет 3 р/д (с 1 месяца)'
      ],
      contraindications: ['Лоперамид детям до 2 лет', 'Антибиотики без показаний', 'Противодиарейные препараты'],
      whenToCall: ['Признаки обезвоживания', 'Кровь в стуле', 'T >38,5°C', 'Рвота >6 часов', 'Отказ от питья']
    },
    {
      condition: 'Бронхообструктивный синдром',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Одышка', 'Свистящие хрипы', 'Кашель', 'Учащенное дыхание', 'Втяжение межреберных промежутков'],
      algorithm: [
        'Оценить степень дыхательной недостаточности',
        'Обеспечить доступ свежего воздуха',
        'Ингаляции с бронхолитиками через небулайзер',
        'При тяжелой обструкции - системные кортикостероиды',
        'При неэффективности - госпитализация'
      ],
      medications: [
        'Ипратропия бромид + фенотерол 1-2 дозы ингаляционно',
        'Сальбутамол 2.5-5 мг ингаляционно через небулайзер',
        'Адреналин 0.01 мг/кг в/м (при тяжелой обструкции)',
        'Кислород 40-60% через маску'
      ],
      contraindications: ['Седативные препараты', 'Муколитики при обструкции', 'Антигистаминные I поколения'],
      whenToCall: ['Одышка в покое', 'Цианоз', 'Участие вспомогательной мускулатуры', 'Снижение SpO2 <90%', 'Отсутствие эффекта от ингаляций']
    },
    {
      condition: 'Острый ларинготрахеит (круп)',
      priority: 'high',
      ageGroup: '6 мес - 6 лет',
      symptoms: ['Лающий кашель', 'Одышка', 'Стридор', 'Осиплость голоса', 'Беспокойство'],
      algorithm: [
        'Оценить степень стеноза (I-IV степень)',
        'Успокоить ребенка, взять на руки',
        'Обеспечить доступ свежего воздуха',
        'Ингаляции с физраствором через небулайзер',
        'При стенозе II-III степени - дексаметазон',
        'При ухудшении - госпитализация'
      ],
      medications: [
        'Дексаметазон 0.6 мг/кг в/м или перорально',
        'Будесонид 0.5-1 мг ингаляционно через небулайзер',
        'Адреналин 0.1 мл/кг ингаляционно (при тяжелом стенозе)'
      ],
      contraindications: ['Седативные препараты', 'Муколитики при стенозе', 'Антигистаминные'],
      whenToCall: ['Стеноз III-IV степени', 'Цианоз', 'Нарушение сознания', 'Остановка дыхания', 'Втяжение яремной ямки']
    },
    {
      condition: 'Эпиглотит',
      priority: 'critical',
      ageGroup: '2-7 лет',
      symptoms: ['Высокая лихорадка', 'Дисфагия', 'Слюнотечение', 'Стридор', 'Поза "треножника"'],
      algorithm: [
        'НЕ осматривать горло (может вызвать полную обструкцию)',
        'Обеспечить проходимость дыхательных путей',
        'Немедленная госпитализация в ЛОР-отделение',
        'При полной обструкции - интубация или трахеостомия',
        'Антибактериальная терапия в стационаре'
      ],
      medications: [
        'Цефтриаксон 50-100 мг/кг/сут в/в',
        'Дексаметазон 0.6 мг/кг в/в',
        'Кислород 100% через маску'
      ],
      contraindications: ['Осмотр горла', 'Седативные препараты', 'Попытки интубации без подготовки'],
      whenToCall: ['При первых признаках эпиглотита', 'Дисфагия', 'Стридор', 'Высокая лихорадка с дисфагией', 'Слюнотечение']
    },
    {
      condition: 'Анафилактический шок',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Кожные проявления', 'Отек гортани', 'Бронхоспазм', 'Гипотония'],
      algorithm: [
        'Прекратить введение аллергена',
        'Уложить с приподнятыми ногами',
        'Ввести адреналин 0.01 мг/кг в/м',
        'Обеспечить проходимость дыхательных путей',
        'В/в доступ, инфузионная терапия'
      ],
      medications: [
        'Адреналин 0.01 мг/кг в/м',
        'Преднизолон 2-5 мг/кг в/в',
        'Супрастин 0.1 мл/год жизни в/м'
      ],
      contraindications: ['Задержка введения адреналина', 'Недооценка тяжести'],
      whenToCall: ['При первых признаках анафилаксии', 'Нарушение дыхания', 'Потеря сознания']
    },
    {
      condition: 'Судорожный синдром',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Тонико-клонические судороги', 'Нарушение сознания', 'Пена изо рта'],
      algorithm: [
        'Обеспечить безопасность ребенка',
        'Повернуть на бок',
        'Очистить дыхательные пути',
        'При судорогах >5 мин - диазепам',
        'Измерить температуру'
      ],
      medications: [
        'Диазепам 0.2-0.3 мг/кг в/в'
      ],
      contraindications: ['Введение препаратов в рот', 'Принудительное открытие рта'],
      whenToCall: ['Судороги >5 минут', 'Повторные судороги', 'Нарушение дыхания', 'Травма головы']
    },
    {
      condition: 'Острая дыхательная недостаточность',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Одышка', 'Цианоз', 'Учащение дыхания', 'Втяжение межреберий', 'Раздувание крыльев носа'],
      algorithm: [
        'Оценить проходимость дыхательных путей',
        'Обеспечить оксигенотерапию (маска, носовые канюли)',
        'При необходимости - сердечно-легочная реанимация',
        'Вызвать скорую помощь',
        'Подготовить к интубации'
      ],
      medications: [
        'Кислород 2-6 л/мин',
        'Сальбутамол 2.5-5 мг ингаляционно',
        'Будесонид 0.5-1 мг ингаляционно'
      ],
      contraindications: ['Задержка с оксигенотерапией', 'Недооценка тяжести'],
      whenToCall: ['При первых признаках ОДН', 'Цианоз', 'Нарушение сознания', 'Остановка дыхания', 'Снижение SpO2 <90%']
    },
    {
      condition: 'Острый аппендицит',
      priority: 'high',
      ageGroup: '2-18 лет',
      symptoms: ['Боль в животе', 'Тошнота, рвота', 'Лихорадка', 'Нарушение аппетита', 'Диарея или запор'],
      algorithm: [
        'Оценить характер и локализацию боли',
        'Проверить симптомы раздражения брюшины',
        'Измерить температуру тела',
        'Пальпация живота (осторожно)',
        'При подозрении - срочная госпитализация'
      ],
      medications: [],
      contraindications: ['Обезболивающие до осмотра хирурга', 'Слабительные', 'Клизмы'],
      whenToCall: ['Подозрение на аппендицит', 'Сильная боль в животе', 'Рвота с болью', 'Лихорадка с болью в животе']
    },
    {
      condition: 'Инородное тело дыхательных путей',
      priority: 'critical',
      ageGroup: 'все возраста',
      symptoms: ['Внезапный кашель', 'Одышка', 'Цианоз', 'Потеря сознания', 'Стридор'],
      algorithm: [
        'Оценить проходимость дыхательных путей',
        'При полной обструкции - прием Геймлиха',
        'У детей до 1 года - удары по спине и толчки в грудь',
        'При потере сознания - СЛР',
        'Немедленно вызвать скорую помощь'
      ],
      medications: [
        'Кислород 100%'
      ],
      contraindications: ['Попытки извлечь пальцами', 'Питье воды'],
      whenToCall: ['При первых признаках обструкции', 'Цианоз', 'Потеря сознания', 'Остановка дыхания']
    },
    {
      condition: 'Травма головы',
      priority: 'high',
      ageGroup: 'все возраста',
      symptoms: ['Головная боль', 'Тошнота, рвота', 'Нарушение сознания', 'Кровотечение', 'Гематома'],
      algorithm: [
        'Оценить уровень сознания (шкала Глазго)',
        'Проверить зрачки (размер, реакция на свет)',
        'Остановить кровотечение',
        'Обеспечить покой',
        'При нарушении сознания - госпитализация'
      ],
      medications: [],
      contraindications: ['Аспирин', 'Анальгин', 'Седативные препараты'],
      whenToCall: ['Потеря сознания', 'Рвота', 'Судороги', 'Нарушение координации', 'Кровотечение из ушей/носа', 'Излитие прозрачной жидкости из носа и уха (ликвор)']
    },
    {
      condition: 'Менингококцемия',
      priority: 'critical',
      ageGroup: 'все возраста',
      symptoms: ['Высокая лихорадка', 'Петехиальная сыпь', 'Геморрагическая сыпь', 'Нарушение сознания', 'Гипотония'],
      algorithm: [
        'Немедленно ввести антибиотик (цефтриаксон)',
        'Обеспечить в/в доступ',
        'Инфузионная терапия (0.9% NaCl)',
        'Кортикостероиды (дексаметазон)',
        'Немедленная госпитализация в реанимацию'
      ],
      medications: [
        'Цефтриаксон 100 мг/кг в/в (максимум 4г)',
        'Дексаметазон 0.6 мг/кг в/в',
        '0.9% NaCl 20 мл/кг в/в болюсно',
        'Кислород 100% через маску'
      ],
      contraindications: ['Задержка с антибиотикотерапией', 'Недооценка тяжести'],
      whenToCall: ['Петехиальная сыпь', 'Высокая лихорадка с сыпью', 'Нарушение сознания', 'Гипотония', 'Подозрение на менингококцемию']
    },
    {
      condition: 'Менингит',
      priority: 'critical',
      ageGroup: 'все возраста',
      symptoms: ['Высокая лихорадка', 'Головная боль', 'Ригидность затылочных мышц', 'Фотофобия', 'Нарушение сознания'],
      algorithm: [
        'Оценить менингеальные симптомы',
        'Немедленно ввести антибиотик (цефтриаксон)',
        'В/в доступ, инфузионная терапия',
        'Кортикостероиды (дексаметазон)',
        'Немедленная госпитализация в реанимацию'
      ],
      medications: [
        'Цефтриаксон 100 мг/кг в/в (максимум 4г)',
        'Дексаметазон 0.6 мг/кг в/в',
        '0.9% NaCl 20 мл/кг в/в болюсно',
        'Кислород 100% через маску'
      ],
      contraindications: ['Задержка с антибиотикотерапией', 'Недооценка тяжести'],
      whenToCall: ['Ригидность затылочных мышц', 'Высокая лихорадка с головной болью', 'Фотофобия', 'Нарушение сознания', 'Подозрение на менингит']
    }
  ];

  const priorityOptions = [
    { value: 'critical', label: 'Критическая', color: '#6B7280', icon: '🔴' },
    { value: 'high', label: 'Высокая', color: '#7C3AED', icon: '🟣' },
    { value: 'medium', label: 'Средняя', color: '#2563EB', icon: '🔵' },
    { value: 'low', label: 'Низкая', color: '#059669', icon: '🟢' }
  ];

  const filteredEmergencyAlgorithms = emergencyAlgorithms.filter(algorithm => {
    const matchesSearch = !emergencySearchTerm || 
      algorithm.condition.toLowerCase().includes(emergencySearchTerm.toLowerCase()) ||
      algorithm.symptoms.some(symptom => 
        symptom.toLowerCase().includes(emergencySearchTerm.toLowerCase())
      );
    
    const matchesPriority = !selectedPriority || algorithm.priority === selectedPriority;
    
    return matchesSearch && matchesPriority;
  });

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2, color: '#2D3748', fontWeight: 700 }}>
          🚨 Неотложная помощь
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem' }}>
          Протоколы неотложной помощи при критических состояниях у детей. 
          Алгоритмы действий при угрожающих жизни состояниях и экстренных ситуациях.
        </Typography>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
          border: '1px solid #7DD3FC',
          borderRadius: 3,
          '& .MuiAlert-icon': {
            color: '#0369A1'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: '#0C4A6E' }}>
          <strong>📋 Важно:</strong> Данные алгоритмы предназначены для экстренных ситуаций. 
          При угрожающих жизни состояниях немедленно вызывайте скорую помощь (103) и следуйте протоколам неотложной помощи.
        </Typography>
      </Alert>


      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)', border: '1px solid #DEE2E6' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              fullWidth
              placeholder="🔍 Поиск по состоянию или симптому..."
              value={emergencySearchTerm}
              onChange={(e) => setEmergencySearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#4A90E2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                maxWidth: 600,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4A90E2',
                    borderWidth: 2,
                  },
                }
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Приоритет</InputLabel>
              <Select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                label="Приоритет"
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4A90E2',
                    borderWidth: 2,
                  },
                }}
              >
                <MenuItem value="">Все приоритеты</MenuItem>
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{option.icon}</span>
                      <span>{option.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        mb: 3,
        p: 2,
        backgroundColor: '#F8F9FA',
        borderRadius: 2,
        border: '1px solid #E9ECEF'
      }}>
        <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 600 }}>
          📊 Найдено алгоритмов: {filteredEmergencyAlgorithms.length}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {filteredEmergencyAlgorithms.map((algorithm, index) => {
          const priority = priorityOptions.find(p => p.value === algorithm.priority);
          return (
          <Card key={index} sx={{ 
            border: `2px solid ${priority?.color || '#E2E8F0'}`,
            borderRadius: 3,
            boxShadow: `0 4px 12px ${priority?.color}20`,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { 
              boxShadow: `0 8px 24px ${priority?.color}30`,
              transform: 'translateY(-2px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${priority?.color} 0%, ${priority?.color}80 100%)`
            }
          }}>
            <CardContent sx={{ p: 3, pt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{priority?.icon}</Typography>
                    <Typography variant="h5" sx={{ color: '#2D3748', fontWeight: 700 }}>
                      {algorithm.condition}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={priority?.label || algorithm.priority} 
                      size="small" 
                      sx={{ 
                        backgroundColor: priority?.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                    <Chip 
                      label={`👶 ${algorithm.ageGroup}`} 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#F0F9FF', 
                        color: '#0369A1',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: '1px solid #BAE6FD'
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mb: 3
              }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔍 Симптомы
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: 2,
                    border: '1px solid #E5E7EB'
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {algorithm.symptoms.map((symptom, i) => (
                        <Chip 
                          key={i}
                          label={symptom} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#6B7280', 
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    💊 Препараты
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#F0F9FF', 
                    borderRadius: 2,
                    border: '1px solid #BAE6FD'
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {algorithm.medications.map((med, i) => (
                        <Chip 
                          key={i}
                          label={med} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#2563EB', 
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  📋 Алгоритм действий
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: '#F8F9FA', 
                  borderRadius: 2,
                  border: '1px solid #E9ECEF'
                }}>
                  <Box component="ol" sx={{ pl: 2, m: 0 }}>
                    {algorithm.algorithm.map((step, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ 
                        mb: 1.5, 
                        fontWeight: 500,
                        color: '#495057',
                        lineHeight: 1.6
                      }}>
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          width: 24,
                          height: 24,
                          backgroundColor: '#4A90E2',
                          color: 'white',
                          borderRadius: '50%',
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          lineHeight: '24px',
                          mr: 1.5,
                          verticalAlign: 'top'
                        }}>
                          {i + 1}
                        </Box>
                        {step}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  ⚠️ Противопоказания
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#FFFBEB', 
                  borderRadius: 2,
                  border: '1px solid #FDE68A'
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {algorithm.contraindications.map((contra, i) => (
                      <Chip 
                        key={i}
                        label={contra} 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#D97706', 
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              <Alert 
                severity="warning" 
                sx={{ 
                  backgroundColor: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
                  border: '2px solid #F59E0B',
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)',
                  '& .MuiAlert-icon': { color: '#D97706', fontSize: '1.5rem' }
                }}
                icon={<WarningIcon sx={{ fontSize: '1.5rem' }} />}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#92400E', display: 'flex', alignItems: 'center', gap: 1 }}>
                  📞 Когда вызывать скорую:
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 1
                }}>
                  {algorithm.whenToCall.map((condition, i) => (
                    <Box key={i} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: 1,
                      border: '1px solid rgba(245, 158, 11, 0.3)'
                    }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        backgroundColor: '#D97706', 
                        borderRadius: '50%',
                        flexShrink: 0
                      }} />
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        color: '#92400E',
                        fontSize: '0.85rem'
                      }}>
                        {condition}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            </CardContent>
          </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default EmergencyCare;
